'use client';

/**
 * "Your Privacy Choices" (owner + counsel work, 2026-10-14). Used on leadpops.com/privacy-choices and rebeliq.com/privacy-choices.
 * 1. Advertising opt-out for this browser: equal-weight Allow / Opt out choice (CCPA regs §7004 symmetry), no verification (§7026(d)).
 *    When the browser sends Global Privacy Control it shows "Opt-Out Request Honored" with the choice set to opted out (§7025(c)(6), §7026(g)).
 * 2. The privacy request form: every right for every U.S. resident, routed by how we got the person's information
 *    (our own business vs. a customer's form, §7050(c)). No account, no ID, no CAPTCHA; minimal fields.
 * Posts to /api/privacy-request on leadpops.com (rebeliq.com passes `endpoint`). The same file lives in rebeliq-website; keep them identical.
 * Styling is self-contained and inherits text color, so it reads on the light LeadPops legal page and the dark rebel iQ page.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { hasGpc, hasOptOutCookie, setAdOptOut } from '@/lib/privacy-optout';
import { reportKnownOptOut } from '@/components/privacy/AdTagGate';

type Props = { endpoint?: string; policyHref?: string };

const REQUESTS: { id: string; label: string; hint?: string; group: 'stop' | 'access' }[] = [
  { id: 'optout_sale_share', label: 'Opt out of sale, sharing and targeted advertising', hint: 'Applies to your contact record and every device, not just this browser.', group: 'stop' },
  { id: 'stop_email', label: 'Stop marketing email', group: 'stop' },
  { id: 'stop_calls_texts', label: 'Stop calls and texts', group: 'stop' },
  { id: 'know', label: 'Tell me what personal information you have about me', group: 'access' },
  { id: 'copy', label: 'Send me a copy of my personal information', group: 'access' },
  { id: 'delete', label: 'Delete my personal information', group: 'access' },
  { id: 'correct', label: 'Correct my personal information', group: 'access' },
  { id: 'third_party_list', label: 'List the specific third parties you disclosed my information to', group: 'access' },
  { id: 'optout_profiling', label: 'Opt out of profiling', hint: 'We do not make significant decisions about people by profiling, but you can still ask.', group: 'stop' },
  { id: 'limit_sensitive', label: 'Limit use of my sensitive personal information', hint: 'We only use it as the law allows, but you can still ask.', group: 'stop' },
  { id: 'appeal', label: 'Appeal a decision on an earlier request', group: 'access' },
];

const US_STATES: [string, string][] = [['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['PR','Puerto Rico'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']];

const CSS = `
.pc{--pc-line:rgba(128,128,140,.38);--pc-soft:rgba(128,128,140,.08);--pc-acc:#7c5cff;--pc-acc-soft:rgba(124,92,255,.12);font-size:16px;line-height:1.55;color:inherit;margin:8px 0 48px}
.pc *{box-sizing:border-box}
.pc h2.pc-h{font-size:1.45rem;line-height:1.25;font-weight:800;margin:0 0 6px;color:inherit}
.pc .pc-sub{margin:0 0 18px;opacity:.82}
.pc-card{border:1px solid var(--pc-line);border-radius:16px;padding:22px;margin:0 0 28px;background:var(--pc-soft)}
.pc-status{display:flex;gap:10px;align-items:flex-start;margin:0 0 16px;padding:12px 14px;border-radius:12px;border:1px solid var(--pc-acc);background:var(--pc-acc-soft);font-weight:700}
.pc-status svg{flex:none;width:20px;height:20px;margin-top:2px;color:var(--pc-acc)}
.pc-status span{font-weight:500;display:block;opacity:.9}
.pc-choice{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:12px 0 10px}
.pc-opt{display:flex;align-items:flex-start;gap:10px;padding:14px;border:1.5px solid var(--pc-line);border-radius:12px;cursor:pointer;background:transparent}
.pc-opt input{margin-top:4px;accent-color:var(--pc-acc);width:18px;height:18px;flex:none}
.pc-opt b{display:block}
.pc-opt small{display:block;opacity:.78;font-size:13.5px;line-height:1.4;margin-top:2px}
.pc-opt[data-on="1"]{border-color:var(--pc-acc);background:var(--pc-acc-soft)}
.pc-opt[aria-disabled="true"]{cursor:not-allowed;opacity:.6}
.pc-note{font-size:14px;opacity:.82;margin:8px 0 0}
.pc-saved{font-size:14px;font-weight:700;margin:10px 0 0;color:var(--pc-acc)}
.pc fieldset{border:0;padding:0;margin:0 0 22px;min-width:0}
.pc legend{font-weight:800;font-size:1.02rem;margin:0 0 8px;padding:0}
.pc-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:0 0 14px}
.pc label.pc-l{display:block;font-weight:600;font-size:14.5px;margin:0 0 6px}
.pc .pc-req{opacity:.7;font-weight:500}
.pc input[type=text],.pc input[type=email],.pc input[type=tel],.pc select,.pc textarea{width:100%;font:inherit;font-size:16px;color:#111;background:#fff;border:1.5px solid var(--pc-line);border-radius:10px;padding:11px 12px;min-height:46px}
.pc select option{color:#111;background:#fff}
.pc input::placeholder,.pc textarea::placeholder{color:#8a8f99;opacity:1}
.pc textarea{min-height:96px;resize:vertical}
.pc input:focus-visible,.pc select:focus-visible,.pc textarea:focus-visible,.pc-opt:focus-within,.pc-check:focus-within{outline:3px solid var(--pc-acc);outline-offset:2px}
.pc-radios{display:grid;gap:8px}
.pc-check{display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border:1px solid var(--pc-line);border-radius:10px;cursor:pointer}
.pc-check input{margin-top:4px;accent-color:var(--pc-acc);width:18px;height:18px;flex:none}
.pc-check small{display:block;opacity:.75;font-size:13.5px;line-height:1.4}
.pc-check[data-on="1"]{border-color:var(--pc-acc);background:var(--pc-acc-soft)}
.pc-checks{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.pc-sub2{font-weight:700;font-size:13px;letter-spacing:.06em;text-transform:uppercase;opacity:.7;margin:14px 0 8px}
.pc-more{margin:10px 0 0;padding:14px;border-left:3px solid var(--pc-acc);background:var(--pc-acc-soft);border-radius:0 10px 10px 0}
.pc-err{color:#c2185b;font-size:14px;font-weight:600;margin:6px 0 0}
.pc-hp{position:absolute!important;left:-10000px!important;width:1px;height:1px;overflow:hidden}
.pc-btn{display:inline-flex;align-items:center;justify-content:center;min-height:50px;padding:0 28px;border-radius:999px;border:0;font:inherit;font-weight:800;font-size:16px;cursor:pointer;color:#fff;background:var(--pc-acc)}
.pc-btn[disabled]{opacity:.6;cursor:progress}
.pc-fine{font-size:14px;opacity:.8;margin:14px 0 0}
.pc-done{border:1.5px solid var(--pc-acc);border-radius:16px;padding:22px;background:var(--pc-acc-soft)}
.pc-done h2{margin:0 0 8px}
.pc-ref{display:inline-block;font-weight:800;font-size:1.25rem;letter-spacing:.04em;padding:4px 12px;border:1px solid var(--pc-acc);border-radius:8px;margin:4px 0 12px}
.pc a{color:inherit;text-decoration:underline;text-underline-offset:3px}
@media (max-width:640px){.pc-choice,.pc-row,.pc-checks{grid-template-columns:1fr}.pc-card{padding:16px}}
`;

export function PrivacyChoices({ endpoint = '/api/privacy-request', policyHref = '/privacy-policy' }: Props) {
  /* ---------- browser opt-out */
  const [ready, setReady] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [optedOut, setOptedOut] = useState(false);
  const [saved, setSaved] = useState('');
  useEffect(() => {
    const g = hasGpc();
    setGpc(g);
    setOptedOut(g || hasOptOutCookie());
    setReady(true);
  }, []);
  const choose = (out: boolean) => {
    if (gpc) return;
    if (out) reportKnownOptOut('toggle', endpoint);
    setAdOptOut(out);
    setOptedOut(out);
    setSaved(out ? 'Saved. Advertising cookies and pixels are off in this browser.' : 'Saved. Advertising cookies and pixels are allowed in this browser.');
  };

  /* ---------- request form */
  const [relationship, setRelationship] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [isAgent, setIsAgent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ ref: string; emailed: boolean; optoutDue: string | null; responseDue: string | null } | null>(null);
  const [failed, setFailed] = useState('');
  const startedAt = useRef(0);
  const doneRef = useRef<HTMLDivElement>(null);
  useEffect(() => { startedAt.current = Date.now(); }, []);
  useEffect(() => { if (done) doneRef.current?.focus(); }, [done]);
  const toggleType = (id: string) => setTypes((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const has = (id: string) => types.includes(id);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const payload = {
      site: location.hostname.replace(/^www\./, ''),
      relationship,
      customerSite: f.get('customerSite'),
      requestTypes: types,
      knowScope: f.get('knowScope'),
      correctDetails: f.get('correctDetails'),
      appealRef: f.get('appealRef'),
      firstName: f.get('firstName'),
      lastName: f.get('lastName'),
      email: f.get('email'),
      phone: f.get('phone'),
      company: f.get('company'),
      state: f.get('state'),
      isAgent,
      agentName: f.get('agentName'),
      agentEmail: f.get('agentEmail'),
      details: f.get('details'),
      company_website: f.get('company_website'),
      gpc: hasGpc(),
      startedAt: startedAt.current,
      elapsedMs: startedAt.current ? Date.now() - startedAt.current : null,
    };
    setBusy(true); setErrors({}); setFailed('');
    try {
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        if (types.includes('optout_sale_share')) { setAdOptOut(true); setOptedOut(true); }
        setDone({ ref: data.ref, emailed: data.emailed === true, optoutDue: data.optoutDue ?? null, responseDue: data.responseDue ?? null });
      } else if (data.errors) {
        setErrors(data.errors);
        requestAnimationFrame(() => (document.querySelector('.pc [aria-invalid="true"]') as HTMLElement | null)?.focus());
      } else {
        setFailed(data.error || 'Something went wrong. Please email privacy@rebeliq.com or call (855) 532-3767.');
      }
    } catch {
      setFailed('We could not send your request. Please email privacy@rebeliq.com or call (855) 532-3767.');
    } finally {
      setBusy(false);
    }
  }

  const err = (k: string) => (errors[k] ? <p className="pc-err" id={`pc-e-${k}`}>{errors[k]}</p> : null);
  const inv = (k: string) => (errors[k] ? { 'aria-invalid': true as const, 'aria-describedby': `pc-e-${k}` } : {});

  return (
    <div className="pc">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* ---------------------------------------------------------------- browser opt-out */}
      <section className="pc-card" aria-labelledby="pc-ads">
        <h2 className="pc-h" id="pc-ads">Advertising cookies and pixels on this browser</h2>
        <p className="pc-sub">
          Our sites use advertising cookies and pixels from companies such as Google, Meta, LinkedIn and HubSpot. Some state laws call this a
          &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal information. We do not sell leads, and we do not sell personal information for money.
        </p>
        {ready && gpc && (
          <p className="pc-status" role="status">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
            <span>
              Opt-Out Request Honored
              <span>Your browser sent a Global Privacy Control signal, so we do not load advertising cookies or pixels, and we treat it as a request to opt out of sale, sharing and targeted advertising.</span>
            </span>
          </p>
        )}
        <div className="pc-choice" role="radiogroup" aria-label="Sale and sharing of my personal information for advertising">
          <label className="pc-opt" data-on={ready && !optedOut ? '1' : '0'} aria-disabled={gpc}>
            <input type="radio" name="pc-ads" checked={ready && !optedOut} disabled={gpc} onChange={() => choose(false)} />
            <span><b>Allow</b><small>Advertising cookies and pixels can load in this browser.</small></span>
          </label>
          <label className="pc-opt" data-on={ready && optedOut ? '1' : '0'} aria-disabled={gpc}>
            <input type="radio" name="pc-ads" checked={ready && optedOut} disabled={gpc} onChange={() => choose(true)} />
            <span><b>Opt out</b><small>Do not sell or share my personal information for advertising.</small></span>
          </label>
        </div>
        {saved && <p className="pc-saved" role="status">{saved}</p>}
        <p className="pc-note">
          {gpc
            ? 'To change this setting, turn off Global Privacy Control in your browser.'
            : 'This setting applies to this browser on this site. To opt out across your devices and for your contact record, choose the opt-out request in the form below. Opting out needs no verification.'}
        </p>
      </section>

      {/* ---------------------------------------------------------------- request form */}
      {done ? (
        <div className="pc-done" ref={doneRef} tabIndex={-1} role="status">
          <h2 className="pc-h">We received your request</h2>
          <p style={{ margin: 0 }}>Your reference number:</p>
          <span className="pc-ref">{done.ref}</span>
          <p>{done.emailed ? 'We sent a confirmation to the email address you gave us. ' : 'Please save this reference number: we could not send a confirmation email just now, but your request is recorded. '}Keep your reference number in case you need to contact us.</p>
          <ul>
            {done.optoutDue && <li>Opt-out requests: we act on them as soon as possible, and no later than {done.optoutDue}.</li>}
            {done.responseDue && <li>Other requests: we confirm your identity by matching what you gave us with the information we hold, and we respond by {done.responseDue}.</li>}
            {relationship === 'customer_form' && <li>Because you submitted your information on one of our customers&rsquo; websites, we are forwarding your request to that customer and will help them respond.</li>}
          </ul>
          <p className="pc-fine">Questions? Email <a href="mailto:privacy@rebeliq.com">privacy@rebeliq.com</a> or call (855) 532-3767.</p>
        </div>
      ) : (
        <section className="pc-card" aria-labelledby="pc-form-h">
          <h2 className="pc-h" id="pc-form-h">Make a privacy request</h2>
          <p className="pc-sub">
            Anyone in the United States can use this form. You do not need an account, and we will never ask for your Social Security number or a government ID. Required fields are marked.
          </p>
          <form onSubmit={submit} noValidate>
            <div className="pc-hp" aria-hidden="true">
              <label>Leave this empty<input type="text" name="company_website" tabIndex={-1} autoComplete="off" /></label>
            </div>

            <fieldset>
              <legend>How did we get your information? <span className="pc-req">(required)</span></legend>
              <div className="pc-radios" {...inv('relationship')}>
                {[
                  ['direct', 'I contacted LeadPops or rebel iQ directly, or I am a customer', 'For example, you visited our site, asked for a demo, used a free tool, or got an email from us.'],
                  ['customer_form', "I filled out a form on a loan officer's, lender's or real estate agent's website", 'That business decides how your information is used. We will forward your request to them and help them respond.'],
                  ['unsure', "I'm not sure", 'We will look in both places.'],
                  ['applicant', 'I applied for a job with us', ''],
                ].map(([v, l, s]) => (
                  <label key={v} className="pc-check" data-on={relationship === v ? '1' : '0'}>
                    <input type="radio" name="relationship" value={v} checked={relationship === v} onChange={() => setRelationship(v)} />
                    <span>{l}{s && <small>{s}</small>}</span>
                  </label>
                ))}
              </div>
              {err('relationship')}
              {(relationship === 'customer_form' || relationship === 'unsure') && (
                <div className="pc-more">
                  <label className="pc-l" htmlFor="pc-cs">Website or business where you submitted your information <span className="pc-req">(optional, helps us find it)</span></label>
                  <input id="pc-cs" type="text" name="customerSite" placeholder="e.g. smithhomeloans.com or Jane Smith, ABC Mortgage" />
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend>What would you like us to do? <span className="pc-req">(required, choose any)</span></legend>
              <div {...inv('requestTypes')}>
                <p className="pc-sub2">Stop or opt out (no verification needed)</p>
                <div className="pc-checks">
                  {REQUESTS.filter((r) => r.group === 'stop').map((r) => (
                    <label key={r.id} className="pc-check" data-on={has(r.id) ? '1' : '0'}>
                      <input type="checkbox" checked={has(r.id)} onChange={() => toggleType(r.id)} />
                      <span>{r.label}{r.hint && <small>{r.hint}</small>}</span>
                    </label>
                  ))}
                </div>
                <p className="pc-sub2">Access, copy, correct or delete (we verify it&rsquo;s you)</p>
                <div className="pc-checks">
                  {REQUESTS.filter((r) => r.group === 'access').map((r) => (
                    <label key={r.id} className="pc-check" data-on={has(r.id) ? '1' : '0'}>
                      <input type="checkbox" checked={has(r.id)} onChange={() => toggleType(r.id)} />
                      <span>{r.label}{r.hint && <small>{r.hint}</small>}</span>
                    </label>
                  ))}
                </div>
              </div>
              {err('requestTypes')}
              {(has('know') || has('copy')) && (
                <div className="pc-more">
                  <span className="pc-l">What time period?</span>
                  <div className="pc-radios">
                    <label className="pc-check"><input type="radio" name="knowScope" value="12_months" defaultChecked /><span>The last 12 months</span></label>
                    <label className="pc-check"><input type="radio" name="knowScope" value="since_2022" /><span>Everything you have, back to January 1, 2022</span></label>
                  </div>
                </div>
              )}
              {has('correct') && (
                <div className="pc-more">
                  <label className="pc-l" htmlFor="pc-cd">What is inaccurate, and what should it say? <span className="pc-req">(required)</span></label>
                  <textarea id="pc-cd" name="correctDetails" {...inv('correctDetails')} />
                  {err('correctDetails')}
                </div>
              )}
              {has('appeal') && (
                <div className="pc-more">
                  <label className="pc-l" htmlFor="pc-ar">Reference number of the request you are appealing</label>
                  <input id="pc-ar" type="text" name="appealRef" placeholder="PR-XXXXXX" {...inv('appealRef')} />
                  {err('appealRef')}
                  <p className="pc-note">Tell us why you disagree in the details box below.</p>
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend>About you</legend>
              <div className="pc-row">
                <div>
                  <label className="pc-l" htmlFor="pc-fn">First name <span className="pc-req">(required)</span></label>
                  <input id="pc-fn" type="text" name="firstName" autoComplete="given-name" {...inv('firstName')} />
                  {err('firstName')}
                </div>
                <div>
                  <label className="pc-l" htmlFor="pc-ln">Last name <span className="pc-req">(required)</span></label>
                  <input id="pc-ln" type="text" name="lastName" autoComplete="family-name" {...inv('lastName')} />
                  {err('lastName')}
                </div>
              </div>
              <div className="pc-row">
                <div>
                  <label className="pc-l" htmlFor="pc-em">Email <span className="pc-req">(required, so we can respond)</span></label>
                  <input id="pc-em" type="email" name="email" autoComplete="email" {...inv('email')} />
                  {err('email')}
                </div>
                <div>
                  <label className="pc-l" htmlFor="pc-ph">Phone {has('stop_calls_texts') ? <span className="pc-req">(required to stop calls and texts)</span> : <span className="pc-req">(optional)</span>}</label>
                  <input id="pc-ph" type="tel" name="phone" autoComplete="tel" {...inv('phone')} />
                  {err('phone')}
                </div>
              </div>
              <div className="pc-row">
                <div>
                  <label className="pc-l" htmlFor="pc-co">Company <span className="pc-req">(optional)</span></label>
                  <input id="pc-co" type="text" name="company" autoComplete="organization" />
                </div>
                <div>
                  <label className="pc-l" htmlFor="pc-st">State you live in <span className="pc-req">(optional)</span></label>
                  <select id="pc-st" name="state" defaultValue="">
                    <option value="">Choose a state</option>
                    {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                  </select>
                </div>
              </div>
              <p className="pc-note">We honor these rights for everyone in the U.S. Your state only tells us which state&rsquo;s contact details to include if you appeal.</p>
            </fieldset>

            <fieldset>
              <legend>Are you making this request for someone else?</legend>
              <label className="pc-check" data-on={isAgent ? '1' : '0'}>
                <input type="checkbox" checked={isAgent} onChange={() => setIsAgent((v) => !v)} />
                <span>Yes, I am an authorized agent<small>Enter the details of the person the request is about above, and your own details here. We will ask you for their signed permission, and we may ask them to confirm with us directly. We do not require a power of attorney.</small></span>
              </label>
              {isAgent && (
                <div className="pc-more">
                  <div className="pc-row" style={{ marginBottom: 0 }}>
                    <div>
                      <label className="pc-l" htmlFor="pc-an">Your name <span className="pc-req">(required)</span></label>
                      <input id="pc-an" type="text" name="agentName" {...inv('agent')} />
                    </div>
                    <div>
                      <label className="pc-l" htmlFor="pc-ae">Your email <span className="pc-req">(required)</span></label>
                      <input id="pc-ae" type="email" name="agentEmail" {...inv('agent')} />
                    </div>
                  </div>
                  {err('agent')}
                </div>
              )}
            </fieldset>

            <fieldset>
              <label className="pc-l" htmlFor="pc-dt">Anything else that helps us find your information or understand your request <span className="pc-req">(optional)</span></label>
              <textarea id="pc-dt" name="details" />
            </fieldset>

            {failed && <p className="pc-err" role="alert">{failed}</p>}
            <button type="submit" className="pc-btn" disabled={busy}>{busy ? 'Sending...' : 'Submit request'}</button>
            <p className="pc-fine">
              We use the information in this form only to handle your request and to keep the record the law requires. See our <a href={`${policyHref}#how-to-submit`}>Privacy Policy</a>.
              You can also email <a href="mailto:privacy@rebeliq.com">privacy@rebeliq.com</a> or call (855) 532-3767.
            </p>
          </form>
        </section>
      )}
    </div>
  );
}
